import { UserPlusIcon } from "@heroicons/react/24/outline";
import ActionButton from "components/buttons/action-button";
import { StatusBadge } from "components/core";
import { PREMIUM_SERVICE_INTEREST_STATUS_BADGE_OPTIONS } from "components/forms/premium-service-interest/schema";
import moment from "moment";
import { FC, MouseEventHandler } from "react";
import { wrapClick } from "utils";

export type PremiumServiceInterestListItem = {
  _id: string;
  code?: string;
  status?: string;
  region?: { name?: string } | null;
  district?: { name?: string } | null;
  customer?: {
    fullName?: string;
    phoneNumber?: string;
    digitalAddress?: string;
  } | null;
  createdAt?: string;
};

export interface PremiumServiceInterestGridCardProps {
  item: PremiumServiceInterestListItem;
  onView: () => void;
  onCancel: () => void;
  onSchedule: () => void;
}

const PremiumServiceInterestGridCard: FC<PremiumServiceInterestGridCardProps> = ({
  item,
  onView,
  onCancel,
  onSchedule,
}) => {
  const handleCardClick: MouseEventHandler<HTMLDivElement> = wrapClick(onView);

  return (
    <div
      key={item._id}
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onView();
        }
      }}
      className="relative overflow-hidden rounded-lg bg-white dark:bg-gray-900 pb-8 shadow cursor-pointer hover:shadow-md transition-shadow px-4 pt-4"
    >
      <dt>
        <div className="absolute rounded-md p-3 bg-primary-600">
          <UserPlusIcon className="h-6 w-6 text-white" aria-hidden="true" />
        </div>
        <p className="ml-16 truncate text-sm font-medium text-gray-500 dark:text-gray-400">
          {item.code || "N/A"}
        </p>
      </dt>
      <dd className="ml-16 flex flex-col pb-6 sm:pb-7 space-y-2 min-h-[7rem]">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {item.createdAt
            ? moment(item.createdAt).format("DD/MM/YYYY [at] hh:mm A")
            : "N/A"}
        </p>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
            {item.customer?.fullName || "N/A"}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {item.customer?.phoneNumber || "N/A"}
          </p>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
        {item.customer?.digitalAddress || "N/A"} · {item.region?.name || "N/A"} · {item.district?.name || "N/A"}
        </p>
      </dd>
      <div className="absolute inset-x-0 bottom-0 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 flex justify-between items-center sm:px-4">
        <StatusBadge
          status={item.status || ""}
          options={PREMIUM_SERVICE_INTEREST_STATUS_BADGE_OPTIONS}
        />
        <div className="space-x-1">
          {item.status === "Pending" && (
            <>
              <ActionButton action="cancel" onClick={onCancel} />
              <ActionButton action="schedule" onClick={onSchedule} />
            </>
          )}
          <ActionButton action="view" onClick={onView} />
        </div>
      </div>
    </div>
  );
};

export default PremiumServiceInterestGridCard;
